import TendencyOverrideControl from "./TendencyOverrideControl.tsx";
type PlayerPlayoffOverrides = Record<string, number>;

type PlayoffOverridesFormProps = {
	godMode: boolean;
	playoffOverrides?: PlayerPlayoffOverrides;
	handleChange: (
		type: string,
		field: string,
		event: {
			target: {
				value: any;
			};
		},
	) => void;
};

const overrideFields = [
	{
		key: "efficiency",
		label: "Playoff Efficiency",
		description:
			"Multiplier applied to shooting efficiency composites during playoff-intensity games. Affects 2P, 3P, and FT accuracy without changing ratings or OVR.",
	},
	{
		key: "usage",
		label: "Playoff Usage",
		description:
			"Multiplier applied to usage during playoff-intensity games. Higher values make the player take a larger offensive role.",
	},
	{
		key: "defense",
		label: "Playoff Defense",
		description:
			"Multiplier applied to defensive composites during playoff-intensity games. Affects overall, interior, and perimeter defense, plus steals and blocks.",
	},
	{
		key: "playmaking",
		label: "Playoff Playmaking",
		description:
			"Multiplier applied to passing during playoff-intensity games. Turnovers are not changed by this field; use Playoff Turnovers for that.",
	},
	{
		key: "rebounding",
		label: "Playoff Rebounding",
		description:
			"Multiplier applied to rebounding during playoff-intensity games.",
	},
	{
		key: "turnovers",
		label: "Playoff Turnovers",
		description:
			"Multiplier applied to turnover tendency during playoff-intensity games. Lower values reduce turnovers; higher values increase turnovers.",
	},
] as const;

const PlayoffOverridesForm = ({
	godMode,
	playoffOverrides,
	handleChange,
}: PlayoffOverridesFormProps) => {
	if (!godMode || __SPORT !== "basketball") {
		return null;
	}

	const handleOverrideChange = (field: string, value: number | undefined) => {
		handleChange("playoffOverrides", field, {
			target: { value },
		});
	};

	const resetAll = () => {
		for (const field of overrideFields) {
			handleChange("playoffOverrides", field.key, {
				target: { value: undefined },
			});
		}
	};

	const hasAnyOverrides = overrideFields.some((field) => {
		const value = playoffOverrides?.[field.key];
		return value !== undefined && value !== 0;
	});

	const hasHighValues = overrideFields.some((field) => {
		const value = playoffOverrides?.[field.key];
		return value !== undefined && value > 2;
	});

	return (
		<div className="playoff-overrides-form">
			<div className="row">
				{overrideFields.map((field) => (
					<div key={field.key} className="col-md-6 mb-3">
						<TendencyOverrideControl
							label={field.label}
							description={field.description}
							fieldKey={field.key}
							value={playoffOverrides?.[field.key]}
							disabled={!godMode}
							onChange={handleOverrideChange}
						/>
					</div>
				))}
			</div>

			{hasAnyOverrides ? (
				<div className="alert alert-info">
					<p className="mb-2">
						Playoff overrides apply only to temporary basketball game simulation
						composites when playoff intensity is active. They do not change OVR,
						POT, displayed ratings, contracts, trades, or development.
					</p>
					<small className="text-muted">
						Blank/0 means no override. 0.9 is a 10% reduction, 1.1 is a 10%
						boost, and 2.0 is a 2x effect.
					</small>
				</div>
			) : null}

			{hasHighValues ? (
				<div className="alert alert-warning">
					Values over 2.0 can create extreme playoff simulation effects.
				</div>
			) : null}

			<button
				type="button"
				className="btn btn-secondary btn-sm"
				onClick={resetAll}
				disabled={!godMode || !hasAnyOverrides}
			>
				Reset All Playoff Overrides
			</button>
		</div>
	);
};

export default PlayoffOverridesForm;
